/* GrowthOS — Create/Edit Account page.
   Same shell as addLead.jsx/addContactsForm.jsx (sticky header, cover with identity +
   readiness card, config-driven form sections below via accountForm.jsx) — just the
   fields/required set match Account instead of Lead/Contact. Photo upload matches
   Lead's/Contact's cover exactly (same `.gos-cl-photo*` widget/handlers) — the
   `*LeadImageCRM` service functions are generic Azure-blob helpers keyed by a
   `${module}/${id}` path (not actually Lead-specific despite the name); this page
   already called them with `module: 'Account'` even before the re-skin (previously
   via the shared `HeaderProfileBlock` + crop-popup), so the capability isn't new —
   only the widget UI changed to match Lead/Contact (simple pick + preview, no crop). */
import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { initValueAccount } from '../../shared/initials';
import apiServices from '../../services/service';
import { toast } from 'react-toastify';
import { isLoadingByKey } from '../../shared/controls/methods/getLoadingState';
import { fetchDataAndUpdateStore, singleRowAddUpdateInStore } from '../../redux/actions';
import AccountForm from './accountForm';
import { validationSchemaAccount } from './validationSchema';
import { getGridDataByModuleKey, getUser } from '../../redux/selector';
import { dropdownPayload } from '../../shared/constants';

const CL_ICONS = {
  chevL: <path d="M15 18l-6-6 6-6" />,
  check: <path d="M20 6 9 17l-5-5" />,
  user: (<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>),
  building: <path d="M3 21h18M6 21V4h9v17M9 8h3M9 12h3M9 16h3M15 21V9h4v12" />,
  campaigns: <path d="M3 11v2a1 1 0 0 0 1 1h2l4 4V6L6 10H4a1 1 0 0 0-1 1ZM15 8a4 4 0 0 1 0 8M18 5a8 8 0 0 1 0 14" />,
  image: (<><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="8.5" cy="9" r="1.5" /><path d="M21 16l-5-5L5 20" /></>),
  refresh: <path d="M21 12a9 9 0 1 1-3-6.7L21 8M21 3v5h-5" />,
  trash: <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V6h12M10 11v6M14 11v6" />,
  checkCircle: (<><circle cx="12" cy="12" r="9" /><path d="M8.5 12l2.5 2.5 4.5-5" /></>),
};
const CLIc = ({ name, size = 16, style }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={style}>
    {CL_ICONS[name] || null}
  </svg>
);

const REQUIRED = [{ key: 'name', label: 'Account Name' }];

const AddAccountsForm = () => {
  const gridData = useSelector(getGridDataByModuleKey('accountList'));
  const handleCloseAddForm = () => {
    if (formData.from === 'view') {
      navigate(`/accounts/viewAccount?id=${formData.id}&pageNo=${gridData?.inputPayload?.pageNumber}`);
    } else {
      navigate('/accounts');
    }
  };
  const stateData = useSelector((state) => state);
  const dispatch = useDispatch();
  let storeLoading = isLoadingByKey(
    stateData,
    'revenueTypeList',
    'leadSourceList',
    'industryList',
    'timeZone'
  );

  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = useSelector(getUser);
  if (!initValueAccount.owner) {
    initValueAccount.owner = userInfo?.user?.display_name;
    initValueAccount.ownerId = userInfo?.user?.userId;
  }
  const [loading, setLoading] = useState(storeLoading);
  const [formData, setFormData] = useState(initValueAccount);
  const [errormsg, setErrorMsg] = useState({});
  const [formEdited, setFormEdited] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [imagefile, setImageFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const photoInputRef = useRef(null);

  const handleUploadImage = async (imagefile) => {
    setImageFile(imagefile);
    setFormEdited(true);
  };

  // show the existing photo when editing
  useEffect(() => {
    if (formData?.id) {
      apiServices
        .getLeadImageCRM(`Account/${formData.id}`)
        .then((url) => setPhotoPreview(url))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.id]);

  const onPhotoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);
    handleUploadImage(file);
    e.target.value = '';
  };
  const removePhoto = () => {
    setPhotoPreview(null);
    setImageFile(null);
    setFormEdited(true);
  };

  const saveUploadImage = async (id) => {
    if (imagefile && id) {
      const newFile = new File([imagefile], id.data, {
        type: imagefile.type,
      });
      await apiServices.uploadLeadImageCRM(newFile, 'Account');
    }
  };

  const editUploadImage = async (id) => {
    if (imagefile && id) {
      const newFile = new File([imagefile], id, {
        type: imagefile.type,
      });
      await apiServices.uploadLeadImageCRM(newFile, 'Account');
    }
  };

  const handleChangeForm = (e) => {
    const { name, value } = e.target;
    let convertedValue = value;
    // Allow only numbers, +, -, (), and space for phone-related fields
    if (['phone', 'mobile', 'alternatePhone'].includes(name)) {
      convertedValue = value.replace(/[^0-9+\-() ]/g, '');
    }
    // Employees: allow only digits and convert to int, or null if empty
    if (name === 'employees') {
      if (value.trim() === '') {
        convertedValue = null;
      } else {
        convertedValue = value.replace(/[^0-9]/g, '');
        if (convertedValue !== '') {
          convertedValue = parseInt(convertedValue, 10);
        }
      }
    }
    // No of Beds: allow only digits but keep as string
    if (name === 'noOfBeds') {
      convertedValue = value.replace(/[^0-9]/g, '');
    }
    // Annual Revenue: allow digits + one dot, max 2 decimal places, or null if empty
    if (name === 'annualRevenue') {
      if (value.trim() === '') {
        convertedValue = null;
      } else {
        convertedValue = value.replace(/[^0-9.]/g, '');
        const parts = convertedValue.split('.');
        if (parts.length > 2) {
          convertedValue = parts[0] + '.' + parts.slice(1).join('');
        }
        if (convertedValue.includes('.')) {
          convertedValue = parts[0] + '.' + (parts[1] || '').substring(0, 2);
        }
        if (!isNaN(convertedValue)) {
          convertedValue = parseFloat(convertedValue);
        }
      }
    }
    setErrorMsg((prevErrorMsg) => ({
      ...prevErrorMsg,
      [name]: undefined,
    }));
    setFormData((prev) => ({
      ...prev,
      [name]: convertedValue,
    }));
    setFormEdited(true);
  };

  const handleChange = (selectedOption, name) => {
    setSelectedOptions((prevState) => ({
      ...prevState,
      [name]: selectedOption,
    }));

    handleLookupDropDownChange(selectedOption.value, selectedOption, name);
  };

  const handleLookupDropDownChange = (selectedId, selectedOption, name) => {
    const selectedValue = selectedOption.label;
    setErrorMsg((prevErrorMsg) => ({
      ...prevErrorMsg,
      [name]: undefined,
    }));
    if (name === 'industryId') {
      setFormData({ ...formData, industryId: selectedId, industry: selectedValue });
      setFormEdited(true);
    } else if (name === 'accountTypeId') {
      setFormData({ ...formData, accountTypeId: selectedId, accountType: selectedValue });
      setFormEdited(true);
    } else if (name === 'ownerId') {
      setFormData({ ...formData, ownerId: selectedId, owner: selectedValue });
      setFormEdited(true);
    } else if (name === 'parentAccountId') {
      setFormData({ ...formData, parentAccountId: selectedId, parentAccount: selectedValue });
      setFormEdited(true);
    }
  };

  const handleSubmit = async (formData) => {
    setLoading(true);
    try {
      const sanitizedData = {
        ...formData,
        website: formData?.website?.replace(/^(https?:\/\/)?(www\.)?/, ''),
        companyLinkedIn: formData?.companyLinkedIn?.replace(/^(https?:\/\/)?(www\.)?/, ''),
      };

      await validationSchemaAccount.validate(sanitizedData, { abortEarly: false });
      handleFormSubmit(sanitizedData);
    } catch (error) {
      setLoading(false);
      const fieldErrors = {};
      error?.inner?.forEach((err) => {
        fieldErrors[err.path] = err.message;
      });
      setErrorMsg(fieldErrors);
      const errorFields = Object.keys(fieldErrors);
      if (errorFields.length > 0) {
        const firstErrorKey = errorFields[0];
        const errorRef = refs[firstErrorKey];
        if (errorRef?.current) {
          errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          errorRef.current.focus();
        }
      }
      console.error('Validation Error:', fieldErrors);
    }
  };
  const refs = {
    name: useRef(null),
  };

  const updateList = async (msg, id) => {
    setLoading(false);
    toast.success(msg, { position: 'top-right' });

    try {
      dispatch(
        singleRowAddUpdateInStore({
          moduleName: 'Account',
          id: formData.type === 'add' ? id : formData.id,
          rowIndex: formData.rowIndex,
        })
      );
      dispatch(
        fetchDataAndUpdateStore({
          key: 'accountDropdown',
          inputData: dropdownPayload,
        })
      );
      if (formData.from === 'view') {
        navigate(`/accounts/viewAccount?id=${formData.id}&pageNo=${gridData?.inputPayload?.pageNumber}`);
      } else {
        navigate('/accounts');
      }
    } catch (error) {
      console.error('Failed to fetch and update Account:', error);
      toast.error('Something went wrong while updating the list.', { position: 'top-right' });
    }
  };

  const handleFormSubmit = (formData) => {
    if (formData.type === 'add') {
      apiServices
        .createAccountList(formData)
        .then((response) => {
          if (response.status === 200) {
            const id = response.data;
            saveUploadImage(response);
            updateList('Account created successfully!', id);
          } else {
            setLoading(false);
            const errorMessages = [];
            for (const key in response.errors) {
              if (response.errors.hasOwnProperty(key)) {
                const messages = response.errors[key];
                errorMessages.push(...messages);
              }
            }
            errorMessages.forEach((errorMessage) => {
              toast.error(errorMessage, { position: 'top-right' });
            });
          }
        })
        .catch((error) => {
          console.error('Error:', error, 'DataFormat:', formData);
          toast.error('An unexpected error occurred.', { position: 'top-right' });
          setLoading(false);
        });
    } else {
      editUploadImage(formData.id);
      apiServices
        .updateAccountList(formData)
        .then((response) => {
          if (response.status === 200) {
            updateList('Account updated successfully!');
          } else {
            setLoading(false);
            const errorMessages = [];
            for (const key in response.errors) {
              if (response.errors.hasOwnProperty(key)) {
                const messages = response.errors[key];
                errorMessages.push(...messages);
              }
            }
            errorMessages.forEach((errorMessage) => {
              toast.error(errorMessage, { position: 'top-right' });
            });
          }
        })
        .catch((error) => {
          console.error('Error:', error, 'DataFormat:', formData);
          toast.error('An unexpected error occurred.', { position: 'top-right' });
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    setLoading(storeLoading);
  }, [storeLoading]);

  useEffect(() => {
    let updatedFormData = {
      ...formData,
      type: location.pathname.includes('addAccount') ? 'add' : 'edit',
    };
    if (location.pathname.includes('editAccount') && location.state) {
      updatedFormData = {
        ...location.state,
        type: 'edit',
      };
    }
    setFormData(updatedFormData);
  }, [location]);

  const isEdit = formData?.type === 'edit';
  const displayName = formData?.name || (isEdit ? 'Account' : 'Untitled account');
  const idSub = [formData?.industry, formData?.accountType].filter(Boolean).join(' · ');
  const filledReq = REQUIRED.filter((r) => {
    const v = formData?.[r.key];
    return v !== null && v !== undefined && String(v).trim() !== '';
  }).length;
  const pct = Math.round((filledReq / REQUIRED.length) * 100);
  const allReady = filledReq === REQUIRED.length;

  return (
    <div className="addlead_container gos-cl">
      {/* sticky header */}
      <div className="gos-cl-header">
        <button type="button" className="gos-cl-back" onClick={handleCloseAddForm} title="Back to Accounts">
          <CLIc name="chevL" size={18} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="gos-cl-htitle">{isEdit ? 'Edit Account' : 'New Account'}</div>
          <div className="gos-cl-hsub">
            Fields marked <span className="gos-cl-reqdot">*</span> are required
          </div>
        </div>
        <button type="button" className="gos-btn gos-btn-ghost" onClick={handleCloseAddForm}>Cancel</button>
        <button
          type="button"
          className="gos-btn gos-btn-primary"
          onClick={() => handleSubmit(formData)}
          disabled={loading || (isEdit && !formEdited)}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Saving…
            </>
          ) : (
            <>
              <CLIc name="check" size={16} /> Save Account
            </>
          )}
        </button>
      </div>

      <div className="gos-cl-scroll">
        <div className="gos-cl3-body">
          {/* cover */}
          <div className="gos-cl3-cover">
            <div className="gos-cl-photo-wrap">
              <input ref={photoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onPhotoPick} />
              <div
                className={'gos-cl-photo' + (photoPreview ? ' has-img' : '')}
                onClick={photoPreview ? undefined : () => photoInputRef.current?.click()}
              >
                {photoPreview ? (
                  <>
                    <img src={photoPreview} alt="Account" />
                    <div className="gos-cl-photo-overlay" onClick={() => photoInputRef.current?.click()}>Change</div>
                  </>
                ) : (
                  <div className="gos-cl-photo-empty">
                    <CLIc name="image" size={24} />
                    <span>Add photo</span>
                  </div>
                )}
              </div>
              {photoPreview ? (
                <div className="gos-cl-photo-actions">
                  <button type="button" className="gos-cl-photo-act" onClick={() => photoInputRef.current?.click()}>
                    <CLIc name="refresh" size={12} /> Change
                  </button>
                  <button type="button" className="gos-cl-photo-act danger" onClick={removePhoto}>
                    <CLIc name="trash" size={12} /> Remove
                  </button>
                </div>
              ) : (
                <div className="gos-cl-photo-hint">JPG or PNG · click to upload</div>
              )}
            </div>

            <div className="gos-cl3-id">
              <div className="gos-cl3-id-eyebrow">{isEdit ? 'Edit Account' : 'New Account'}</div>
              <div className={'gos-cl3-id-name' + (displayName && formData?.name ? '' : ' ph')}>{displayName}</div>
              <div className="gos-cl3-id-sub">{idSub || 'Add company and ownership details below'}</div>
              <div className="gos-cl3-id-chips">
                <span className="gos-lr-chip"><CLIc name="user" size={13} /> Owner: {formData?.owner || 'Unassigned'}</span>
                {formData?.accountType ? <span className="gos-lr-chip"><CLIc name="building" size={13} /> {formData.accountType}</span> : null}
                {formData?.parentAccount ? <span className="gos-lr-chip"><CLIc name="campaigns" size={13} /> {formData.parentAccount}</span> : null}
              </div>
            </div>

            <div className={'gos-cl3-ready' + (allReady ? ' full' : '')}>
              <div className="gos-cl3-ready-top">
                <span className="gos-cl3-ready-label">Account readiness</span>
                <span className="gos-cl3-ready-pct">{pct}%</span>
              </div>
              <div className="gos-cl3-bar"><div style={{ width: pct + '%' }} /></div>
              <div className={'gos-cl3-ready-sub' + (allReady ? ' ok' : '')}>
                {allReady ? (
                  <><CLIc name="checkCircle" size={13} /> All required fields complete</>
                ) : (
                  `${filledReq} of ${REQUIRED.length} required fields filled`
                )}
              </div>
            </div>
          </div>

          <AccountForm
            formData={formData}
            selectedOptions={selectedOptions}
            handleSubmit={handleSubmit}
            handleChangeForm={handleChangeForm}
            handleChange={handleChange}
            errormsg={errormsg}
            refs={refs}
          />
        </div>
      </div>
    </div>
  );
};

export default AddAccountsForm;
